import { ChainPropagation } from 'types';

/**
 * Success copy for the package actions that now write the whole chain.
 *
 * The modals promise the change applies to everything, so the confirmation has to say how far it
 * actually went. A skipped package is a live package left on the old setting, which is the exact
 * failure chain propagation exists to remove: reporting it is what stops the modal over-promising.
 */

const SKIP_REASON_LABELS: Record<string, string> = {
  NO_PAYMENT_METHOD: 'no card on file',
  NON_STRIPE_PAYMENT_METHOD: 'set to cash or check',
};

const skippedSentence = (packagesSkipped: number, skippedReasons: string[]): string => {
  const labels = skippedReasons.map(reason => SKIP_REASON_LABELS[reason] ?? 'not eligible').join(' or ');
  const subject =
    packagesSkipped === 1 ? 'One package was skipped' : `${packagesSkipped} packages were skipped`;
  return `${subject}: ${labels}.`;
};

export const autoPaySuccessMessage = (autoPay: boolean, propagation: ChainPropagation | null): string => {
  const verb = autoPay ? 'enabled' : 'disabled';

  if (!propagation) {
    return `Auto-pay ${verb}.`;
  }

  const { packagesUpdated, packagesSkipped, skippedReasons } = propagation;

  if (packagesSkipped === 0) {
    // Counts packages, not invoices. An invoice with a charge already in flight, or without a
    // card of its own, is skipped, so naming invoices here would assert more than was written.
    // The modal states the invoice scope before the change is applied.
    return packagesUpdated === 1
      ? `Auto-pay ${verb} for this package.`
      : `Auto-pay ${verb} for ${packagesUpdated} packages.`;
  }

  const total = packagesUpdated + packagesSkipped;
  return `Auto-pay ${verb} on ${packagesUpdated} of ${total} packages. ${skippedSentence(
    packagesSkipped,
    skippedReasons,
  )}`;
};

export const paymentMethodSuccessMessage = (propagation: ChainPropagation | null): string => {
  // Packages only, for the same reason as above: an invoice mid-charge keeps its previous card.
  if (!propagation || propagation.packagesUpdated <= 1) {
    return 'Payment method updated for this package.';
  }
  return `Payment method updated for ${propagation.packagesUpdated} packages.`;
};
